export interface FoodicsIntegrationStatus {
  connected: boolean;
  business_name?: string;
  is_active?: boolean;
  connected_at?: string;
  /** API sets true when APP_ENV is local/development and FOODICS_DEV_TOKEN is set (install skips OAuth). */
  install_uses_dev_token?: boolean;
}

export interface FoodicsBranch {
  id: string;
  name: string;
  name_localized?: string;
  reference?: string;
  type?: number;
}

export interface FoodicsBranchesResponse {
  message: string;
  count: number;
  data: FoodicsBranch[];
}

export interface FoodicsBranchMapping {
  id: number;
  internal_id: number;
  internal_name: string;
  external_id: string;
  external_code: string;
  external_name: string;
  is_active: boolean;
  synced_at: string;
}

export interface FoodicsBranchMappingsResponse {
  message: string;
  integration_id: number;
  business_name: string;
  count: number;
  data: FoodicsBranchMapping[];
}

export interface CreateFoodicsBranchMappingBody {
  internal_id: number;
  external_id: string;
  external_code?: string;
  external_name?: string;
  external_data?: Record<string, any>;
}

export interface FoodicsPaymentMethod {
  id: string;
  name: string;
  name_localized?: string;
  code?: string;
  type?: number;
}

export interface FoodicsPaymentMethodsResponse {
  message: string;
  count: number;
  data: FoodicsPaymentMethod[];
}

export interface FoodicsPaymentMethodMapping {
  id: number;
  internal_id: number;
  internal_name: string;
  external_id: string;
  external_code: string;
  external_name: string;
  is_active: boolean;
  synced_at: string;
}

export interface FoodicsPaymentMethodMappingsResponse {
  message: string;
  integration_id: number;
  business_name: string;
  count: number;
  data: FoodicsPaymentMethodMapping[];
}

export interface CreateFoodicsPaymentMethodMappingBody {
  internal_id: number;
  external_id: string;
  external_code?: string;
  external_name?: string;
  external_data?: Record<string, any>;
}

export interface FoodicsMenuCategoryEntity {
  external_id: string;
  name: { en?: string; ar?: string };
  /** Foodics category reference / code when available */
  sku?: string | null;
  already_imported: boolean;
  is_new?: boolean;
  raw: Record<string, any>;
}

export interface FoodicsMenuProductEntity {
  external_id: string;
  name: { en?: string; ar?: string };
  description?: { en?: string; ar?: string };
  price?: string | number | null;
  /** Foodics product cost (fixed COGS when not using recipe). May be string from API. */
  cost?: string | number | null;
  /** Foodics product SKU — stored on Menu so order import CSV lines match. */
  sku?: string;
  type?: number | null;
  category?: {
    id?: string;
    sku?: string | null;
    title?: { en?: string; ar?: string };
  } | null;
  already_imported: boolean;
  is_new?: boolean;
  ingredient_count?: number;
  ingredients?: unknown[];
  raw: Record<string, any>;
}

export interface FoodicsModifierOptionEntity {
  external_id: string | null;
  name: { en?: string; ar?: string };
  price?: string | number | null;
  sku?: string;
  modifier?: {
    id?: string;
    title?: { en?: string; ar?: string };
  } | null;
  already_imported: boolean;
  is_new?: boolean;
  raw: Record<string, any>;
}

export interface FoodicsInventoryItemEntity {
  external_id: string | null;
  name: { en?: string; ar?: string };
  sku: string;
  is_product: boolean;
  cost?: number | null;
  costing_method?: number | null;
  barcode?: string | null;
  minimum_level?: string | number | null;
  maximum_level?: string | number | null;
  par_level?: string | number | null;
  storage_to_ingredient_factor?: number | null;
  storage_unit?: string | null;
  ingredient_unit?: string | null;
  ingredient_count?: number;
  ingredients?: unknown[];
  /** Linked supplier from Foodics (default / primary). */
  supplier?: { id?: string; name?: string } | null;
  suppliers?: Array<{ id?: string; name?: string }> | null;
  supplier_id?: string | null;
  already_imported: boolean;
  is_new?: boolean;
  raw: Record<string, any>;
}

/**
 * An item is classified as production if it has ingredients.
 * Items without ingredients are plain inventory (raw materials).
 */
export const isProductionItem = (item: FoodicsInventoryItemEntity): boolean =>
  Array.isArray(item.ingredients) && item.ingredients.length > 0;

export interface FoodicsMenuEntitiesResponse {
  message: string;
  data: {
    categories: FoodicsMenuCategoryEntity[];
    categories_pagination?: {
      from?: number;
      to?: number;
      per?: number;
      total?: number;
      current?: number;
    } | null;
    products: FoodicsMenuProductEntity[];
    products_pagination?: {
      from?: number;
      to?: number;
      per?: number;
      total?: number;
      current?: number;
    } | null;
    modifier_options?: FoodicsModifierOptionEntity[];
    modifier_options_pagination?: {
      from?: number;
      to?: number;
      per?: number;
      total?: number;
      current?: number;
      last_page?: number;
    } | null;
    /** True when include_modifier_options was requested but Foodics /modifier_options failed. */
    modifier_options_fetch_failed?: boolean;
    inventory_items?: FoodicsInventoryItemEntity[];
    inventory_pagination?: {
      from?: number;
      to?: number;
      per?: number;
      total?: number;
      current?: number;
      last_page?: number;
    } | null;
    /** True when include_inventory was requested but Foodics /inventory_items failed (check logs, scopes, token). */
    inventory_fetch_failed?: boolean;
    summary?: {
      categories?: {
        total_in_page?: number;
        imported_in_page?: number;
        not_imported_in_page?: number;
      };
      products?: {
        total_in_page?: number;
        imported_in_page?: number;
        not_imported_in_page?: number;
      };
      inventory_items?: {
        total_in_page?: number;
        imported_in_page?: number;
        not_imported_in_page?: number;
      };
      modifier_options?: {
        total_in_page?: number;
        imported_in_page?: number;
        not_imported_in_page?: number;
      };
    };
  };
}

export interface ImportFoodicsMenuEntitiesBody {
  categories?: Array<{
    id: string;
    name?: { en?: string; ar?: string };
    sku?: string;
  }>;
  products?: Array<{
    id: string;
    name?: { en?: string; ar?: string };
    sku?: string;
    price?: string | number | null;
    cost?: number | null;
    type?: number | null;
    category?: {
      id?: string;
      sku?: string;
      reference?: string;
      code?: string;
      title?: { en?: string; ar?: string };
    } | null;
  }>;
  inventory_items?: Array<{
    id: string;
    sku?: string;
    is_product?: boolean;
    cost?: number;
    name?: { en?: string; ar?: string };
    ingredient_unit?: string;
    storage_unit?: string;
    storage_to_ingredient_factor?: number | null;
    minimum_level?: string | number;
    maximum_level?: string | number;
    ingredients?: unknown[];
    supplier?: { id?: string; name?: string };
    suppliers?: Array<{ id?: string; name?: string }>;
    supplier_id?: string;
  }>;
  recipe_product_ids?: string[];
  recipe_modifier_option_ids?: string[];
  branch_id?: number;
  modifier_options?: Array<{
    id: string;
    name?: { en?: string; ar?: string };
    sku?: string;
    price?: string | number | null;
  }>;
}

export interface FoodicsOrdersPreviewResponse {
  message: string;
  source: string;
  data: any;
  filters_applied?: Record<string, string>;
}

export interface ImportFoodicsOrdersBody {
  branch_id: number;
  start_date: string;
  end_date: string;
}

export interface ImportFoodicsOrdersResponse {
  message: string;
  data: {
    journey_id: number;
    branch_id: number;
    start_date: string;
    end_date: string;
  };
}

export interface ImportFoodicsSuppliersBody {
  /** When provided, only Foodics suppliers with these ids are imported. Omit to import all. */
  external_ids?: string[];
}

export interface ImportFoodicsSuppliersResponse {
  message: string;
  data: {
    imported: number;
    updated: number;
    skipped: number;
    total_in_foodics?: number;
  };
}

export interface FoodicsSupplierEntity {
  external_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  supplier_tax_number: string | null;
  already_imported: boolean;
  raw: Record<string, any>;
}

export interface FoodicsSuppliersResponse {
  message: string;
  data: {
    suppliers: FoodicsSupplierEntity[];
    pagination?: {
      from?: number;
      to?: number;
      per?: number;
      total?: number;
      current?: number;
      last_page?: number;
    } | null;
  };
}
